import React, { useMemo, useState } from "react";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { CloseIcon } from "../../../Common/SvgIcons/WorkflowIcons"; // adjust path if needed
import NivoTextParser from "./NivoTextParser";
import nivoTheme from "./nivoTheme";

const RADIAL_COLORS = ["#e8c1a0", "#f47560", "#f1e15b", "#e8a838", "#61cdbb", "#97e3d5"];

const getBackendColor = (source, label) => {
  if (!source || typeof source !== "object") return undefined;

  const normalizedLabel = String(label ?? "").trim();
  const lowerLabel = normalizedLabel.toLowerCase();
  const candidates = [
    source.color,
    source.colour,
    source.hexColor,
    source.hex_code,
    source.hexCode,
    normalizedLabel ? source[`${normalizedLabel} Color`] : undefined,
    lowerLabel ? source[`${lowerLabel}_color`] : undefined,
    lowerLabel ? source[`${lowerLabel}Color`] : undefined,
  ];

  return candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim().length > 0
  )?.trim();
};

export default function RadialChart({ data, align, title, showLegend = false }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const chartColorMeta = useMemo(() => {
    const normalizedData = Array.isArray(data) ? data : [];
    const categoryColors = new Map();
    const seriesColors = new Map();
    const categories = [];

    normalizedData.forEach((series, seriesIndex) => {
      const seriesLabel = String(series?.id ?? `Series ${seriesIndex + 1}`);
      const seriesColor = getBackendColor(series, seriesLabel);

      if (seriesColor) {
        seriesColors.set(seriesLabel, seriesColor);
      }

      (Array.isArray(series?.data) ? series.data : []).forEach((item) => {
        if (item?.x !== undefined && !categories.includes(item.x)) {
          categories.push(item.x);
        }

        const categoryLabel = String(item?.x ?? "");
        const pointColor = getBackendColor(item, categoryLabel);
        if (pointColor && !categoryColors.has(categoryLabel)) {
          categoryColors.set(categoryLabel, pointColor);
        }
      });
    });

    const hasPointColors = categoryColors.size > 0;
    const hasSeriesColors = seriesColors.size > 0;
    const fallbackCategoryColors = new Map(
      categories.map((category, index) => [
        String(category),
        RADIAL_COLORS[index % RADIAL_COLORS.length],
      ])
    );
    const fallbackSeriesColors = new Map(
      normalizedData.map((series, index) => [
        String(series?.id ?? `Series ${index + 1}`),
        RADIAL_COLORS[index % RADIAL_COLORS.length],
      ])
    );
    const legendItems = hasPointColors
      ? categories.map((category) => ({
          label: String(category),
          color:
            categoryColors.get(String(category)) ||
            fallbackCategoryColors.get(String(category)),
        }))
      : hasSeriesColors
        ? normalizedData.map((series, index) => {
            const label = String(series?.id ?? `Series ${index + 1}`);
            return {
              label,
              color: seriesColors.get(label) || fallbackSeriesColors.get(label),
            };
          })
        : categories.map((category) => ({
            label: String(category),
            color: fallbackCategoryColors.get(String(category)),
          }));

    return {
      categoryColors,
      seriesColors,
      fallbackCategoryColors,
      fallbackSeriesColors,
      hasSeriesColors,
      legendItems,
    };
  }, [data]);

  const {
    categoryColors,
    seriesColors,
    fallbackCategoryColors,
    fallbackSeriesColors,
    hasSeriesColors,
    legendItems,
  } = chartColorMeta;

  const renderChart = () => (
    <ResponsiveRadialBar
      data={data}
      padding={0.4}
      startAngle={0}
      endAngle={270}
      cornerRadius={2}
      margin={{ top: 40, right: align ? 40 : 120, bottom: 40, left: 40 }}
      radialAxisStart={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
      circularAxisOuter={null}
      colors={(bar) => {
        const categoryLabel = String(bar?.category ?? bar?.data?.x ?? "");
        const seriesLabel = String(bar?.groupId ?? "");

        return (
          getBackendColor(bar?.data, categoryLabel) ||
          categoryColors.get(categoryLabel) ||
          seriesColors.get(seriesLabel) ||
          (hasSeriesColors
            ? fallbackSeriesColors.get(seriesLabel)
            : fallbackCategoryColors.get(categoryLabel)) ||
          RADIAL_COLORS[0]
        );
      }}
      theme={nivoTheme}
      legends={[]}
    />
  );

  const renderChartWithLegend = () => showLegend && legendItems.length > 0 ? (
    <div className="dataset-radial-chart-layout">
      <div className="dataset-radial-chart-canvas">{renderChart()}</div>
      <aside className="dataset-radial-chart-legend" aria-label={`${title || "Radial chart"} legend`}>
        {legendItems.map((item) => (
          <div className="dataset-radial-chart-legend__item" key={item.label} title={item.label}>
            <i style={{ backgroundColor: item.color }} />
            <span>{item.label}</span>
          </div>
        ))}
      </aside>
    </div>
  ) : renderChart();

  return (
    <>
      <div
        className="chart"
        style={{
          height: 300,
          position: "relative",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsModalOpen(true)}
      >
        <NivoTextParser title={title} />

        {isHovered && (
          <button
            type="button"
            className="plain-button chart-expand-control"
            aria-label={`Expand ${title || "radial chart"}`}
            title="Expand"
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        )}

        {renderChartWithLegend()}
      </div>

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 6,
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="pt-0 pe-0 container-card w-75 w-md-100"
            style={{
              backgroundColor: "var(--bg-color8)",
              padding: "50px",
              height: "70%",
              position: "relative",
              overflow: "visible",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close expanded chart"
              className="plain-button text-danger"
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(false);
              }}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                cursor: "pointer",
                zIndex: 3,
              }}
            >
              <CloseIcon className="wf-icon text-secondary" />
            </button>

            <div
              className="p-3"
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              {title && (
                <div style={{ height: "50px" }}>
                  <NivoTextParser title={title} />
                </div>
              )}
              <div style={{ height: title ? "calc(100% - 50px)" : "100%" }}>
                {renderChartWithLegend()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
